import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

export const baseUrl = process.env.REACT_APP_ENDPOINT;

// Axios interceptor for handling 401 Unauthorized globally
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // Clear localStorage
      localStorage.clear();
      // Clear all cookies
      document.cookie.split(";").forEach((cookie) => {
        const eqPos = cookie.indexOf("=");
        const name =
          eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
        document.cookie =
          name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
      });
      // Explicitly clear buzzusr cookie (for localhost)
      document.cookie =
        "buzzusr=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=localhost";
      // Redirect to login page
      window.location = "/auth/signin";
      return; // Prevent further error handling
    }
    return Promise.reject(error);
  }
);

export const verifyUser = createAsyncThunk(
  "user/verifyOtp",
  async (paymentDetails, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${baseUrl}/landlord/payment`,
        { ...paymentDetails },
        { withCredentials: true }
      );
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const loginUser = createAsyncThunk(
  "user/loginUser",
  async (loginDetails, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${baseUrl}/user/login`,
        { ...loginDetails },
        { withCredentials: true }
      );
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const getProfile = createAsyncThunk(
  "profile/fetch",
  async ({ userId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${baseUrl}/user?id=${userId}`, {
        withCredentials: true,
      });
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const editProfile = createAsyncThunk(
  "profile/update",
  async ({ name, email, phone }, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        `${baseUrl}/user`,
        { name, email, phone },
        { withCredentials: true }
      );
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const editProfileImage = createAsyncThunk(
  "profile/updateImage",
  async ({ image, remove }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      if (remove) {
        formData.append("remove", "true");
      }
      if (image) {
        formData.append("image", image);
      }
      const response = await axios.put(`${baseUrl}/user/profile`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const forgotPassword = createAsyncThunk(
  "user/forgotPassword",
  async (phone, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${baseUrl}/user/forgot-password`,
        { ...phone },
        { withCredentials: true }
      );
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const verifyOtp = createAsyncThunk(
  "user/forgotPassword/verifyOtp",
  async ({ phone, otp }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${baseUrl}/user/verify-reset-otp`,
        { phone, otp },
        { withCredentials: true }
      );
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const resetPassword = createAsyncThunk(
  "user/resetPassword",
  async ({ newPassword, phone }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${baseUrl}/user/reset-password`,
        { newPassword, phone },
        { withCredentials: true }
      );
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const changeUserPassword = createAsyncThunk(
  "user/changePassword",
  async ({ oldPassword, newPassword }, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        `${baseUrl}/user/password`,
        { oldPassword, newPassword },
        { withCredentials: true }
      );
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const handleLogout = createAsyncThunk(
  "user/logout",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${baseUrl}/user/logout`, {
        withCredentials: true,
      });
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const caretaker = createAsyncThunk(
  "caretaker/add",
  async ({ newCaretaker, propertyId }, { rejectWithValue, dispatch }) => {
    try {
      console.log("received", newCaretaker);
      const response = await axios.post(
        `${baseUrl}/caretaker?id=${propertyId}`,
        { ...newCaretaker },
        { withCredentials: true }
      );
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const admin = createAsyncThunk(
  "admin/add",
  async (admin, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.post(
        `${baseUrl}/admins`,
        { ...admin },
        { withCredentials: true }
      );
      dispatch(getProperty(caretaker.property));
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const tenant = createAsyncThunk(
  "tenant/add",
  async ({ newTenant, houseId, isNewTenant }, { rejectWithValue }) => {
    try {
      if (isNewTenant) {
        const response = await axios.post(
          `${baseUrl}/tenant/new?id=${houseId}`,
          { ...newTenant },
          { withCredentials: true }
        );
        return response.data;
      } else {
        console.log("received existing tenant", newTenant);
        const response = await axios.post(
          `${baseUrl}/tenant/existing?id=${houseId}`,
          { ...newTenant },
          { withCredentials: true }
        );
        return response.data;
      }
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const getInvoiceCreatedIndividual = createAsyncThunk(
  "prompt/checkInvoice",
  async ({ houseId }, { rejectWithValue }) => {
    try {
      const month = new Date().getMonth() + 1; // Months are 0-indexed in JavaScript
      const year = new Date().getFullYear(); // Get the current year
      const response = await axios.get(
        `${baseUrl}/invoice/tenant/only?id=${houseId}&month=${month}&year=${year}`,
        { withCredentials: true }
      );
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const promptPayment = createAsyncThunk(
  "prompt/tenantPayment",
  async ({ paymentDetails, houseId }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${baseUrl}/invoice/tenant/only?id=${houseId}`,
        { ...paymentDetails },
        { withCredentials: true }
      );
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const createSTK = createAsyncThunk(
  "prompt/STK",
  async ({ paymentDetails }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${baseUrl}/payments/stk`,
        { ...paymentDetails },
        { withCredentials: true }
      );
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);
export const checkSTKStatus = createAsyncThunk(
  "prompt/STKStatus",
  async ({ checkoutRequestId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${baseUrl}/payments/stk/status?checkoutRequestId=${checkoutRequestId}`,
        { withCredentials: true }
      );
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const fetchTenants = createAsyncThunk(
  "tenants/fetch",
  async ({ property_id }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${baseUrl}/tenant?id=${property_id}`, {
        withCredentials: true,
      });
      return response?.data?.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const updateTenant = createAsyncThunk(
  "tenant/update",
  async ({ updateInfo, houseId }, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        `${baseUrl}/tenant/update?id=${houseId}`,
        { ...updateInfo },
        { withCredentials: true }
      );
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const relocateTenant = createAsyncThunk(
  "tenant/relocate",
  async ({ userID, new_house_id }, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        `${baseUrl}/tenant/relocate?id=${userID}`,
        { new_house_id },
        { withCredentials: true }
      );
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const deleteTenant = createAsyncThunk(
  "tenant/delete",
  async ({ userID }, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        `${baseUrl}/tenant/vacate?id=${userID}`,
        {},
        { withCredentials: true }
      );
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const fetchCaretakers = createAsyncThunk(
  "caretaker/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${baseUrl}/caretakers`, {
        withCredentials: true,
      });
      return response.data.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const addProperty = createAsyncThunk(
  "property/add",
  async ({ propertyDetails }, { rejectWithValue, dispatch }) => {
    try {
      const formData = new FormData();
      console.log("propertyDetails", propertyDetails);
      formData.append("name", propertyDetails.name);
      formData.append("location", propertyDetails.location);
      formData.append(
        "service_charge",
        parseInt(propertyDetails.service_charge, 10)
      );
      formData.append(
        "service_charge_type",
        propertyDetails.service_charge_type
      );
      formData.append(
        "water_charge",
        parseInt(propertyDetails.water_charge, 10)
      );
      formData.append("water_charge_type", propertyDetails.water_charge_type);
      formData.append(
        "garbage_charge",
        parseInt(propertyDetails.garbage_charge, 10)
      );
      formData.append("late_fee", parseInt(propertyDetails.late_fee, 10));
      formData.append("late_fee_type", propertyDetails.late_fee_type);
      formData.append("late_due_date", propertyDetails.late_due_date);
      formData.append("mgmt_fee", parseInt(propertyDetails.mgmt_fee, 10));
      formData.append("mgmt_fee_type", propertyDetails.mgmt_fee_type);
      formData.append("images", propertyDetails.images[0]);
      formData.append("images", propertyDetails.images[1]);
      formData.append("images", propertyDetails.images[2]);

      if (propertyDetails?.paybillAccountNumber) {
        formData.append(
          "paybillAccountNumber",
          propertyDetails.paybillAccountNumber
        );
        formData.append("businessNumber", propertyDetails.businessNumber);
      } else {
        formData.append("tillNumber", propertyDetails.tillNumber);
      }

      const response = await axios.post(`${baseUrl}/property`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.status === 201) {
        dispatch(getAllProperties());
      }

      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const editProperty = createAsyncThunk(
  "property/edit",
  async ({ propertyDetails, propertyId }, { rejectWithValue, dispatch }) => {
    try {
      const formData = new FormData();
      console.log("propertyDetails", propertyDetails);
      formData.append("name", propertyDetails.name);
      formData.append("location", propertyDetails.location);
      formData.append(
        "service_charge",
        parseInt(propertyDetails.service_charge, 10)
      );
      formData.append(
        "service_charge_type",
        propertyDetails.service_charge_type
      );
      formData.append(
        "water_charge",
        parseInt(propertyDetails.water_charge, 10)
      );
      formData.append("water_charge_type", propertyDetails.water_charge_type);
      formData.append(
        "garbage_charge",
        parseInt(propertyDetails.garbage_charge, 10)
      );
      formData.append("late_fee", parseInt(propertyDetails.late_fee, 10));
      formData.append("late_fee_type", propertyDetails.late_fee_type);
      formData.append("late_due_date", propertyDetails.late_due_date);
      formData.append("mgmt_fee", parseInt(propertyDetails.mgmt_fee, 10));
      formData.append("mgmt_fee_type", propertyDetails.mgmt_fee_type);
      formData.append("images", propertyDetails.images[0]);
      formData.append("images", propertyDetails.images[1]);
      formData.append("images", propertyDetails.images[2]);

      formData.append(
        "paybillAccountNumber",
        propertyDetails?.payment_details?.paybill?.paybillAccountNumber
      );
      formData.append(
        "businessNumber",
        propertyDetails?.payment_details?.paybill?.businessNumber
      );

      formData.append(
        "tillNumber",
        propertyDetails?.payment_details?.till?.tillNumber
      );

      const response = await axios.put(
        `${baseUrl}/property?id=${propertyId}`,
        formData,
        {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (response.status === 201) {
        dispatch(getAllProperties());
      }

      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const getAllProperties = createAsyncThunk(
  "getProperties/all",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${baseUrl}/property/user`, {
        withCredentials: true,
      });
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const getProperty = createAsyncThunk(
  "property/get",
  async ({ id, token }, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await axios.get(`${baseUrl}/properties/${id}/houses`, {
        ...config,
        withCredentials: true,
      });

      const houses = response.data.data;
      const data = { houses };

      const sortedHouses = data.houses.sort(
        (a, b) => a.house_number - b.house_number
      );
      const sortedData = { houses: sortedHouses };

      return sortedData;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const updateProperty = createAsyncThunk(
  "property/update",
  async ({ propertyDetails, token }, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      const response = await axios.patch(
        `${baseUrl}/properties/${propertyDetails.property_id}`,
        { ...propertyDetails },
        { ...config, withCredentials: true }
      );
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const deleteProperty = createAsyncThunk(
  "property/delete",
  async (property_id, { rejectWithValue }) => {
    try {
      const response = await axios.delete(
        `${baseUrl}/properties/${property_id}`,
        { withCredentials: true }
      );
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const getAllHouses = createAsyncThunk(
  "getHouses/all",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${baseUrl}/house/property?id=${id}`, {
        withCredentials: true,
      });

      try {
        const houses = response.data.data;

        // Apply alpha-numerical sorting to houses by house number
        const sortedHouses = houses.sort((a, b) => {
          const houseNumberA = a.house_no || "";
          const houseNumberB = b.house_no || "";

          // Extract the numeric part from the house numbers
          const numA = parseInt(houseNumberA.replace(/^\D+/g, ""), 10) || 0;
          const numB = parseInt(houseNumberB.replace(/^\D+/g, ""), 10) || 0;

          // Extract letter parts
          const lettersA = houseNumberA.replace(/\d/g, "").toLowerCase();
          const lettersB = houseNumberB.replace(/\d/g, "").toLowerCase();

          // If both are pure numbers, sort numerically
          if (lettersA === "" && lettersB === "") {
            return numA - numB;
          }

          // If one is pure number, place it first
          if (lettersA === "") return -1;
          if (lettersB === "") return 1;

          // If both are alphanumeric, sort by numeric part first, then by letters
          if (numA !== numB) return numA - numB;
          return lettersA.localeCompare(lettersB);
        });

        console.log("the sorted houses are", sortedHouses);
        return { ...response.data, data: sortedHouses };
      } catch (error) {
        console.log("Error sorting houses:", error);
        // Return original response if sorting fails
        return response.data;
      }
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const addHouse = createAsyncThunk(
  "house/add",
  async ({ house, propertyId }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${baseUrl}/house?id=${propertyId}`,
        { ...house },
        { withCredentials: true }
      );
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const addHouses = createAsyncThunk(
  "house/bulk",
  async ({ housesDetails, propertyId }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${baseUrl}/house/bulk?id=${propertyId}`,
        { ...housesDetails },
        { withCredentials: true }
      );
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const updateHouse = createAsyncThunk(
  "house/update",
  async ({ house, houseId }, { rejectWithValue }) => {
    try {
      console.log("content received", house, "house id", houseId);
      const response = await axios.put(
        `${baseUrl}/house?id=${houseId}`,
        { ...house },
        { withCredentials: true }
      );
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const deleteHouse = createAsyncThunk(
  "house/delete",
  async ({ deleteId }, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`${baseUrl}/house?id=${deleteId}`, {
        withCredentials: true,
      });
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const sendAlerts = createAsyncThunk(
  "alerts",
  async ({ smsdetails, property, token }, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      const response = await axios.post(
        `${baseUrl}/properties/sms/generic/${property}`,
        { ...smsdetails },
        { ...config, withCredentials: true }
      );
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const sendInvoiceAlerts = createAsyncThunk(
  "alerts/invoices",
  async ({ selectedPropertyId, token }, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      const response = await axios.post(
        `${baseUrl}/properties/${selectedPropertyId}/send-invoice`,
        { selectedPropertyId },
        { ...config, withCredentials: true }
      );
      console.log("response is", response.data);
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

//for V1
export const sendSms = createAsyncThunk(
  "sms",
  async ({ details, token }, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      const response = await axios.post(
        `${baseUrl}/properties/sms/send`,
        { ...details },
        { ...config, withCredentials: true }
      );
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

//for V2
export const sendCustomBulkSms = createAsyncThunk(
  "sms/bulk",
  async (
    { message, tenant_ids, message_type, selectedProperty },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.post(
        `${baseUrl}/tenant/send-tenant-bulk-messages`,
        { message, tenant_ids, message_type, selectedProperty },
        { withCredentials: true }
      );
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const listInvoices = createAsyncThunk(
  "invoices/rent",
  async ({ property_id, month, year, token }, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      const monthString = parseInt(month, 10);
      const yearString = parseInt(year, 10);
      const propertyId = parseInt(property_id, 10);
      const response = await axios.get(
        `${baseUrl}/properties/${propertyId}/invoices/rent?month=${monthString}&year=${yearString}`,
        { ...config, withCredentials: true }
      );

      try {
        const invoices = response.data.data;
        console.log("MY RESPONSE", response.data);
        const sortedInvoices = invoices
          .filter(
            (invoice) =>
              invoice.house?.house_number &&
              invoice.house.house_number.trim() !== ""
          )
          .sort((a, b) => {
            const houseNumberA = a.house.house_number || "";
            const houseNumberB = b.house.house_number || "";

            // Extract the numeric part from the house numbers
            const numA = parseInt(houseNumberA.replace(/^\D+/g, ""), 10) || 0;
            const numB = parseInt(houseNumberB.replace(/^\D+/g, ""), 10) || 0;

            // Compare based on numeric part
            if (numA !== numB) {
              return numA - numB;
            }

            // If numeric parts are equal, compare the full strings as a fallback
            return houseNumberA.localeCompare(houseNumberB);
          });

        const toSendAsResponse = { ...response.data, data: sortedInvoices };
        console.log("WHAT AM I SENDING", toSendAsResponse);

        return toSendAsResponse;
      } catch (error) {
        console.log("error", error);
        return response.data;
      }
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const listWaterInvoices = createAsyncThunk(
  "invoices/water",
  async ({ property_id, month, year }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${baseUrl}/invoice/water?id=${property_id}&month=${month}&year=${year}`,
        { withCredentials: true }
      );

      if (response?.data?.data?.waterInvoices) {
        try {
          const readings = response?.data?.data?.waterInvoices;

          // Sort meter readings by house number
          const sortedReadings = readings.sort((a, b) => {
            const houseNumberA = a.house?.house_no || "";
            const houseNumberB = b.house?.house_no || "";

            // Extract numeric part from house numbers
            const numA = parseInt(houseNumberA.replace(/^\D+/g, ""), 10) || 0;
            const numB = parseInt(houseNumberB.replace(/^\D+/g, ""), 10) || 0;

            // Extract letter parts
            const lettersA = houseNumberA.replace(/\d/g, "").toLowerCase();
            const lettersB = houseNumberB.replace(/\d/g, "").toLowerCase();

            // If both are pure numbers, sort numerically
            if (lettersA === "" && lettersB === "") {
              return numA - numB;
            }

            // If one is pure number, place it first
            if (lettersA === "") return -1;
            if (lettersB === "") return 1;

            // If both are alphanumeric, sort by numeric part first, then by letters
            if (numA !== numB) return numA - numB;
            return lettersA.localeCompare(lettersB);
          });

          // Return sorted data
          return { ...response.data, meterReadings: sortedReadings };
        } catch (error) {
          console.log("Error sorting water meter readings:", error);
          return response.data;
        }
      } else {
        console.log("here is the response", response.data);
        return response.data;
      }
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const createInvoice = createAsyncThunk(
  "invoice/water",
  async ({ propertyId, updatedReadings }, { rejectWithValue }) => {
    try {
      console.log("received", propertyId);
      const response = await axios.post(
        `${baseUrl}/invoice/unit?id=${propertyId}`,
        updatedReadings,
        { withCredentials: true }
      );
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const listRentInvoices = createAsyncThunk(
  "invoices/rent/list",
  async ({ property_id, month, year }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${baseUrl}/invoice?id=${property_id}&month=${month}&year=${year}`,
        { withCredentials: true }
      );

      // Helper function to sort by house number
      const sortByHouseNumber = (items) => {
        if (!items || !Array.isArray(items)) return [];
        return items.sort((a, b) => {
          const houseNumberA = a.tenant?.house?.house_no || "";
          const houseNumberB = b.tenant?.house?.house_no || "";

          const numA = parseInt(houseNumberA.replace(/^\D+/g, ""), 10) || 0;
          const numB = parseInt(houseNumberB.replace(/^\D+/g, ""), 10) || 0;

          const lettersA = houseNumberA.replace(/\d/g, "").toLowerCase();
          const lettersB = houseNumberB.replace(/\d/g, "").toLowerCase();

          if (lettersA === "" && lettersB === "") {
            return numA - numB;
          }
          if (lettersA === "") return -1;
          if (lettersB === "") return 1;
          if (numA !== numB) return numA - numB;
          return lettersA.localeCompare(lettersB);
        });
      };

      if (response?.data?.data?.invoices) {
        try {
          const invoices = response?.data?.data?.invoices;
          const ignoredInvoices = response?.data?.data?.ignoredInvoices || [];

          const sortedInvoices = sortByHouseNumber(invoices);
          const sortedIgnoredInvoices = sortByHouseNumber(ignoredInvoices);

          return {
            ...response.data,
            data: {
              invoices: sortedInvoices,
              ignoredInvoices: sortedIgnoredInvoices,
            },
          };
        } catch (error) {
          console.log("Error sorting invoices:", error);
          return response.data;
        }
      } else {
        return response.data;
      }
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const createRentInvoice = createAsyncThunk(
  "invoice/rent",
  async ({ propertyId, month, year, invoices = [] }, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        `${baseUrl}/invoice/rent?id=${propertyId}&month=${month}&year=${year}`,
        { invoices },
        { withCredentials: true }
      );
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const getAllInvoicesForLandlord = createAsyncThunk(
  "invoices/landlord/all",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${baseUrl}/invoice/landlord`, {
        withCredentials: true,
      });

      const invoices = response?.data?.data?.invoices || [];

      const sortedInvoices = invoices.sort((a, b) => {
        const noA = a.tenant?.house?.house_no || "";
        const noB = b.tenant?.house?.house_no || "";

        // Extract numeric and letter parts
        const numA = parseInt(noA.replace(/^\D+/g, ""), 10) || 0;
        const numB = parseInt(noB.replace(/^\D+/g, ""), 10) || 0;
        const lettersA = noA.replace(/\d/g, "").toLowerCase();
        const lettersB = noB.replace(/\d/g, "").toLowerCase();

        // If both are pure numbers, sort numerically
        if (lettersA === "" && lettersB === "") {
          return numA - numB;
        }

        // If one is pure number, place it first
        if (lettersA === "") return -1;
        if (lettersB === "") return 1;

        // If both are alphanumeric, sort by numeric part first, then by letters
        if (numA !== numB) return numA - numB;
        return lettersA.localeCompare(lettersB);
      });

      return { ...response.data, data: { invoices: sortedInvoices } };
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const fetchPaymentStatistics = createAsyncThunk(
  "payments/fetch",
  async (property_id, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${baseUrl}/payments/statistics/${property_id}`,
        { withCredentials: true }
      );
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const getAllExpenses = createAsyncThunk(
  "getExpenses/all",
  async ({ propertyId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${baseUrl}/expenses/all?id=${propertyId}`,
        { withCredentials: true }
      );
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const getAllExpensesFromDate = createAsyncThunk(
  "getExpenses/all",
  async ({ property_id, month, year }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${baseUrl}/expenses?id=${property_id}&month=${month}&year=${year}`,
        { withCredentials: true }
      );
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const createExpense = createAsyncThunk(
  "expenses/create",
  async ({ expenseDetails, propertyId }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${baseUrl}/expenses?id=${propertyId}`,
        { ...expenseDetails },
        { withCredentials: true }
      );
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const fetchPayments = createAsyncThunk(
  "paymentsdata/fetch",
  async ({ propertyId, month, year }, { rejectWithValue }) => {
    try {
      const url =
        month && year
          ? `${baseUrl}/payments?id=${propertyId}&page=1&limit=10&month=${month}&year=${year}`
          : `${baseUrl}/payments?id=${propertyId}&page=1&limit=10`;

      const response = await axios.get(url, { withCredentials: true });
      const payments = response.data.data || [];

      const sortedPayments = payments.sort((a, b) => {
        const noA = a.tenant?.house?.house_no || "";
        const noB = b.tenant?.house?.house_no || "";
        // numeric parts
        const numA = parseInt(noA.replace(/^\D+/g, ""), 10) || 0;
        const numB = parseInt(noB.replace(/^\D+/g, ""), 10) || 0;
        // detect any letters
        const lettersA = noA.replace(/\d/g, "");
        const lettersB = noB.replace(/\d/g, "");

        // if both are pure numbers, sort numerically
        if (!lettersA && !lettersB) {
          return numA - numB;
        }
        // if one is pure number, put it first
        if (!lettersA) return -1;
        if (!lettersB) return 1;
        // else alphanumeric: compare numeric part, then full string
        if (numA !== numB) return numA - numB;
        return noA.localeCompare(noB);
      });

      return { ...response.data, data: sortedPayments };
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const fetchPendingPayments = createAsyncThunk(
  "payments/pending",
  async ({ property_id, token }, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await axios.get(
        `${baseUrl}/payments/properties/${property_id}?payment_status=0`,
        { ...config, withCredentials: true }
      );

      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const confirmPayments = createAsyncThunk(
  "payment/confirm",
  async ({ paymentId }, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        `${baseUrl}/payments/approve?id=${paymentId}`,
        { withCredentials: true }
      );
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);
export const reversePayments = createAsyncThunk(
  "payment/reverse",
  async ({ paymentId }, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        `${baseUrl}/payments/reverse?payment_id=${paymentId}`,
        { withCredentials: true }
      );
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const housesReports = createAsyncThunk(
  "housesreports/fetch",
  async (
    { selectedPropertyId, token, selectedMonth, selectedYear },
    { rejectWithValue }
  ) => {
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      const response = await axios.get(
        `${baseUrl}/properties/${selectedPropertyId}/reports?month=${selectedMonth}&year=${selectedYear}`,
        { ...config, withCredentials: true }
      );
      console.log("response", response.data);
      return response.data.report;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const createManualPayment = createAsyncThunk(
  "payments/manual",
  async ({ paymentDetails, invoice_id }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${baseUrl}/v1/payments/manual/${invoice_id}`,
        { ...paymentDetails },
        { withCredentials: true }
      );
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const tenantsData = createAsyncThunk(
  "tenant/data",
  async ({ id, token }, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      const response = await axios.get(`${baseUrl}/tenants/${id}/data`, {
        ...config,
        withCredentials: true,
      });
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const sendMesssageTenant = createAsyncThunk(
  "alerts/tenant",
  async ({ data, token }, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      const response = await axios.post(
        `${baseUrl}/tenants/send/sms`,
        { ...data },
        { ...config, withCredentials: true }
      );
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const listTenantInvoices = createAsyncThunk(
  "invoice/tenant/list",
  async ({ details }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${baseUrl}/invoice/tenant/unpaid?month=${details?.month}&year=${details?.year}`,
        { withCredentials: true }
      );
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const listInvoicesPaid = createAsyncThunk(
  "invoice/paid/list",
  async (
    { property_id, selectedMonth, selectedYear, token },
    { rejectWithValue }
  ) => {
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await axios.get(
        `${baseUrl}/properties/${property_id}/paid-invoices?month=${selectedMonth}&year=${selectedYear}`,
        { ...config, withCredentials: true }
      );
      console.log(
        "response",
        response.data,
        "property_id",
        property_id,
        "months",
        selectedMonth,
        "year",
        selectedYear
      );
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const listInvoicesUnpaid = createAsyncThunk(
  "invoice/unpaid/list",
  async ({ property_id, selectedMonth, selectedYear }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${baseUrl}/invoice/unpaid?id=${property_id}&month=${selectedMonth}&year=${selectedYear}`,
        { withCredentials: true }
      );

      if (response?.data?.data?.invoices) {
        try {
          const invoices = response.data.data.invoices;

          // Sort invoices by house number
          const sortedInvoices = invoices.sort((a, b) => {
            const houseNumberA = a.tenant?.house?.house_no || "";
            const houseNumberB = b.tenant?.house?.house_no || "";

            // Extract numeric part from house numbers
            const numA = parseInt(houseNumberA.replace(/^\D+/g, ""), 10) || 0;
            const numB = parseInt(houseNumberB.replace(/^\D+/g, ""), 10) || 0;

            // Extract letter parts
            const lettersA = houseNumberA.replace(/\d/g, "").toLowerCase();
            const lettersB = houseNumberB.replace(/\d/g, "").toLowerCase();

            // If both are pure numbers, sort numerically
            if (lettersA === "" && lettersB === "") {
              return numA - numB;
            }

            // If one is pure number, place it first
            if (lettersA === "") return -1;
            if (lettersB === "") return 1;

            // If both are alphanumeric, sort by numeric part first, then by letters
            if (numA !== numB) return numA - numB;
            return lettersA.localeCompare(lettersB);
          });

          return { ...response.data, data: { invoices: sortedInvoices } };
        } catch (error) {
          console.log("Error sorting unpaid invoices:", error);
          return response.data;
        }
      }

      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const listAllPropertiesInvoices = createAsyncThunk(
  "invoice/all/list",
  async ({ user_id, month, year, token }, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      const unpaidInvoicesResponse = await axios.get(
        `${baseUrl}/properties/unpaid-invoices/all?month=${month}&year=${year}`,
        { ...config, withCredentials: true }
      );
      const paidInvoicesResponse = await axios.get(
        `${baseUrl}/properties/paid-invoices/all?month=${month}&year=${year}`,
        { ...config, withCredentials: true }
      );
      const data = {
        paidInvoices: {
          paidInvoices: paidInvoicesResponse.data.paidInvoices,
          totalPaidInvoices: paidInvoicesResponse.data.totalPaidInvoices,
        },
        unpaidInvoices: {
          unpaidInvoices: unpaidInvoicesResponse.data.unpaidInvoices,
          totalUnpaidInvoices: unpaidInvoicesResponse.data.totalUnpaidInvoices,
        },
      };
      console.log("response", data);
      return data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const createLandlordPayment = createAsyncThunk(
  "payment/landlord/create",
  async ({ details }, { rejectWithValue, dispatch }) => {
    try {
      console.log("received", details);

      const response = await axios.post(`${baseUrl}/payments`, details, {
        withCredentials: true,
      });
      // if (response.status === 201) {
      //   dispatch(fetchPayments({ property_id: propertyId, token }));
      // }
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const fetchTenantPayments = createAsyncThunk(
  " payments/tenant/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${baseUrl}/payments/tenant`, {
        withCredentials: true,
      });
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const createTenantPayment = createAsyncThunk(
  "payment/tenant/create",
  async ({ details }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.post(`${baseUrl}/payments/tenant`, details, {
        withCredentials: true,
      });

      if (response.status === 201) {
        dispatch(fetchTenantPayments());
      }
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const sendRentReminderSms = createAsyncThunk(
  "sms/rent/reminder",
  async ({ property_id, month, year }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${baseUrl}/invoice/send-invoices?id=${property_id}&month=${month}&year=${year}`,
        {},
        { withCredentials: true }
      );
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

// Save draft invoice edits (individual invoice field edits before finalization)
export const saveDraftInvoiceEdits = createAsyncThunk(
  "invoice/draft-edit",
  async ({ invoices }, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        `${baseUrl}/invoice/draft-edit`,
        { invoices },
        { withCredentials: true }
      );
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue({ message: err.message });
    }
  }
);
